import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { NormalizeTrailingSlash } from '@/components/NormalizeTrailingSlash';
import { ScrollToTop } from '@/components/ScrollToTop';
import App from './App';
import './index.css';
import { configureAmplify } from './lib/amplify-config';
import { markAmplifyConfigured } from './lib/amplify-ready';
import { installAmplifySubscriptionRejectionHandler } from './lib/amplifySubscribe';
import { clearChunkReloadFlag } from './lib/lazyWithRetry';

const AMPLIFY_BOOT_TIMEOUT_MS = 8_000;

installAmplifySubscriptionRejectionHandler();

function removeBootScreen() {
  document.getElementById('app-boot')?.remove();
  window.dispatchEvent(new Event('shopgnd-app-mounted'));
}

/** Auth/hub paths are noindex — drop crawl shell immediately so a failed chunk cannot leave "Page loading". */
function stripCrawlShellOnAppRoutes() {
  const path = window.location.pathname || '/';
  if (
    path === '/' ||
    path === '' ||
    path.startsWith('/grenada/') ||
    path.startsWith('/shop') ||
    path.startsWith('/product') ||
    path.startsWith('/business') ||
    path.startsWith('/vendors') ||
    path.startsWith('/classifieds') ||
    path.startsWith('/property') ||
    path.startsWith('/deals')
  ) {
    return;
  }
  document.getElementById('seo-crawl')?.remove();
  document.getElementById('seo-breadcrumbs')?.remove();
  document.getElementById('route-lcp-shell')?.remove();
}

clearChunkReloadFlag();

const root = document.getElementById('root');

function mountApp() {
  if (!root) return;
  document.documentElement.setAttribute('data-app-ready', '1');
  removeBootScreen();
  stripCrawlShellOnAppRoutes();
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ErrorBoundary>
          <ScrollToTop />
          <NormalizeTrailingSlash />
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </React.StrictMode>,
  );
  void Promise.all([
    import('@fontsource-variable/dm-sans/wght.css'),
    import('@fontsource/dm-serif-display/400.css'),
    import('@fontsource/fraunces/400.css'),
    import('@fontsource/fraunces/600.css'),
  ]);
}

// Mount React immediately — never block the UI on Amplify network/setup.
mountApp();

void Promise.race([
  configureAmplify(Amplify),
  new Promise<void>((_, reject) => {
    window.setTimeout(
      () => reject(new Error(`Amplify configure timed out after ${AMPLIFY_BOOT_TIMEOUT_MS}ms`)),
      AMPLIFY_BOOT_TIMEOUT_MS,
    );
  }),
]).catch((err) => {
  console.error('[ShopGND] Amplify configure failed or timed out', err);
  markAmplifyConfigured();
});
